import React, { useState, useEffect } from 'react';
import {
    Container,
    Button,
    Typography,
    Box,
} from '@mui/material';
import { useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import Page from '../../../components/Page';
import { PATH_DASHBOARD } from '../../../routes/paths';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import useSettings from '../../../hooks/useSettings';
import useTrade from '../../../hooks/useTrade';
import Iconify from '../../../components/Iconify';
import HedgingViewModal from './HedgingViewModal';
import HedgingViewTable from './HedgingViewTable';
import { getHedgingViews } from '../../../redux/slices/operator';

const HedgingView = () => {
    const { themeStretch } = useSettings();
    const location = useLocation();
    const dispatch = useDispatch();

    // WebSocket for real-time updates
    const { localSocket } = useTrade();

    // Redux state
    const { hedgingViews, hedgingViewsPagination, isLoading } = useSelector((state) => state?.operator);
    const { quotesData, previousQuote } = useSelector((state) => state?.trade);

    // Local state for modal
    const [showAddForm, setShowAddForm] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Fetch hedging views on component mount and when page/pageSize changes
    useEffect(() => {
        dispatch(getHedgingViews({
            page,
            limit: pageSize
        }));
    }, [dispatch, page, pageSize]);

    // Real-time subscription for hedging view instruments
    useEffect(() => {
        if (!hedgingViews || hedgingViews.length === 0 || !localSocket) return;

        // Extract instrument tokens from both scripts in all hedging views
        const instrumentTokens = [];
        hedgingViews.forEach(hedge => {
            if (hedge.script1?.scriptData?.instrument_token) {
                instrumentTokens.push(parseInt(hedge.script1.scriptData.instrument_token, 10));
            }
            if (hedge.script2?.scriptData?.instrument_token) {
                instrumentTokens.push(parseInt(hedge.script2.scriptData.instrument_token, 10));
            }
        });

        // Remove duplicates
        const uniqueTokens = [...new Set(instrumentTokens)];

        if (uniqueTokens.length === 0) return;

        const sendSubscribeMessage = () => {
            if (localSocket && localSocket.readyState === WebSocket.OPEN) {
                localSocket.send(JSON.stringify({
                    event: 'subscribe',
                    data: uniqueTokens
                }));
                // console.log('Subscribed to hedging view instruments:', uniqueTokens);
            }
        };

        // Subscribe immediately if connection is open, or when it opens
        if (localSocket.readyState === WebSocket.OPEN) {
            setTimeout(sendSubscribeMessage, 100);
        } else {
            const originalOnOpen = localSocket.onopen;
            localSocket.onopen = (event) => {
                if (originalOnOpen) originalOnOpen.call(localSocket, event);
                setTimeout(sendSubscribeMessage, 100);
            };
        }

        // Cleanup: unsubscribe when component unmounts or dependencies change
        return () => {
            if (localSocket && localSocket.readyState === WebSocket.OPEN && uniqueTokens.length > 0) {
                localSocket.send(JSON.stringify({
                    event: 'unsubscribe',
                    data: uniqueTokens
                }));
                // console.log('Unsubscribed from hedging view instruments:', uniqueTokens);
            }
        };
    }, [hedgingViews, localSocket]);

    const handleAddHedgingView = () => {
        setShowAddForm(true);
    };

    const handleCloseModal = () => {
        setShowAddForm(false);
        // Refresh the list after modal closes (in case new item was added)
        dispatch(getHedgingViews({
            page,
            limit: pageSize
        }));
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setPage(1); // Reset to first page when changing page size
    };

    const hasData = hedgingViews && hedgingViews.length > 0;

    return (
        <Page title="Hedging View">
            <Container maxWidth={themeStretch ? false : 'lg'}>
                <HeaderBreadcrumbs
                    heading="Hedging View"
                    links={[
                        { name: 'Dashboard', href: `/${location.pathname.slice(1).split('/')[0]}` },
                        // {
                        //     name: 'hedging-view',
                        //     href: PATH_DASHBOARD.report.hedgingView,
                        // },
                    ]}

                    action={
                        <Button
                            variant="contained"
                            startIcon={<Iconify icon="eva:plus-fill" />}
                            onClick={handleAddHedgingView}
                            sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                }
                            }}
                        >
                            Add Hedging View
                        </Button>
                    }
                />

                {hasData ? (
                    <HedgingViewTable
                        data={hedgingViews}
                        pagination={hedgingViewsPagination}
                        loading={isLoading}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        currentPage={page}
                        pageSize={pageSize}
                    />
                ) : (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Iconify
                            icon="mdi:chart-line"
                            sx={{
                                fontSize: 80,
                                color: 'text.disabled',
                                mb: 2
                            }}
                        />
                        <Typography variant="h5" color="text.secondary" gutterBottom>
                            No Hedging Views Created
                        </Typography>
                        <Typography variant="body1" color="text.disabled" sx={{ mb: 3 }}>
                            Create your first hedging view to start managing risk positions
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<Iconify icon="eva:plus-fill" />}
                            onClick={handleAddHedgingView}
                            sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                }
                            }}
                        >
                            Add Hedging View
                        </Button>
                    </Box>
                )}

                {/* Hedging View Modal Component */}
                <HedgingViewModal
                    open={showAddForm}
                    onClose={handleCloseModal}
                />
            </Container>
        </Page>
    );
};

export default HedgingView;
